<template>
  <div class="home-page">
    <section class="hero">
      <div class="hero-content">
        <p class="hero-eyebrow">Cruise Signals</p>
        <h1 class="hero-headline">Track cruise prices. Book when it's right for you.</h1>
        <p class="hero-subline">Compare lines, find deals, and get alerts when prices drop.</p>
        <div class="hero-planner-bar">
          <div class="planner-field">
            <i class="fas fa-compass planner-field-icon" aria-hidden="true"></i>
            <label class="planner-field-label" for="planner-destination">Where to</label>
            <select id="planner-destination" v-model="plannerDestination" class="planner-select">
              <option value="">Any Destination</option>
              <option v-for="d in plannerDestinations" :key="d.id" :value="d.id">{{ d.label }}</option>
            </select>
          </div>
          <div class="planner-field">
            <i class="fas fa-calendar-alt planner-field-icon" aria-hidden="true"></i>
            <label class="planner-field-label" for="planner-month">When</label>
            <select id="planner-month" v-model="plannerMonth" class="planner-select">
              <option value="">Anytime</option>
              <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </div>
          <button type="button" class="planner-submit-btn" @click="submitPlanner">
            <i class="fas fa-search" aria-hidden="true"></i>
            Find Cruises
          </button>
        </div>
        <div class="hero-quick-chips">
          <span class="quick-chips-label">Popular:</span>
          <button
            v-for="chip in quickChips"
            :key="chip.id"
            type="button"
            class="quick-chip"
            @click="goToDestination(chip.id)"
          >
            {{ chip.label }}
          </button>
        </div>
      </div>
      <div class="hero-visual" aria-hidden="true">
        <img
          class="hero-visual-art"
          src="/illustrations/hero-ship.svg"
          alt=""
          width="480"
          height="360"
          decoding="async"
        />
      </div>
    </section>

    <MarketPulseStrip />
    <FeaturedCruiseShelf />
    <DestinationGrid />

    <HotDealShelf :affiliate-enabled="affiliateEnabled" />
    <BestDealShelf :affiliate-enabled="affiliateEnabled" />

    <section class="how-it-works">
      <h2 class="section-title">How it works</h2>
      <div class="steps">
        <div class="step">
          <span class="step-icon"><i class="fas fa-chart-line"></i></span>
          <span class="step-label">Track</span>
          <span class="step-desc">See price history for any cruise</span>
        </div>
        <div class="step-arrow" aria-hidden="true"><i class="fas fa-chevron-right"></i></div>
        <div class="step">
          <span class="step-icon"><i class="fas fa-balance-scale"></i></span>
          <span class="step-label">Compare</span>
          <span class="step-desc">Compare lines and find the best value</span>
        </div>
        <div class="step-arrow" aria-hidden="true"><i class="fas fa-chevron-right"></i></div>
        <div class="step">
          <span class="step-icon"><i class="fas fa-bell"></i></span>
          <span class="step-label">Alert</span>
          <span class="step-desc">Get notified when prices drop</span>
        </div>
      </div>
    </section>

    <section class="features">
      <h2 class="section-title">What you can do</h2>
      <div class="feature-grid">
        <router-link to="/cruises" class="feature-card">
          <span class="feature-icon"><i class="fas fa-ship"></i></span>
          <h3 class="feature-title">Browse cruises</h3>
          <p class="feature-desc">Track prices across cruise lines and destinations.</p>
          <span class="feature-link">Browse cruises <i class="fas fa-arrow-right"></i></span>
        </router-link>
        <router-link to="/best-deals" class="feature-card">
          <span class="feature-icon"><i class="fas fa-tag"></i></span>
          <h3 class="feature-title">Best deals</h3>
          <p class="feature-desc">Curated best price, value, and discount.</p>
          <span class="feature-link">See best deals <i class="fas fa-arrow-right"></i></span>
        </router-link>
        <router-link to="/hot-deals" class="feature-card">
          <span class="feature-icon"><i class="fas fa-fire"></i></span>
          <h3 class="feature-title">Hot deals</h3>
          <p class="feature-desc">Recent price drops and newly listed offers.</p>
          <span class="feature-link">View hot deals <i class="fas fa-arrow-right"></i></span>
        </router-link>
        <router-link to="/comparison" class="feature-card">
          <span class="feature-icon"><i class="fas fa-chart-bar"></i></span>
          <h3 class="feature-title">Compare cruise lines</h3>
          <p class="feature-desc">Average prices by cruise line.</p>
          <span class="feature-link">Compare <i class="fas fa-arrow-right"></i></span>
        </router-link>
        <!-- CP-125: Price alerts spans the full row so the grid never leaves an orphan half -->
        <router-link to="/manage-alerts" class="feature-card feature-card--wide">
          <span class="feature-icon"><i class="fas fa-bell"></i></span>
          <h3 class="feature-title">Price alerts</h3>
          <p class="feature-desc">Get notified when a cruise hits your target price.</p>
          <span class="feature-link">Manage alerts <i class="fas fa-arrow-right"></i></span>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import HotDealShelf from '@/components/HotDealShelf.vue'
import { affiliateState } from '@/utils/affiliateFlag'
import BestDealShelf from '@/components/BestDealShelf.vue'
import MarketPulseStrip from '@/components/MarketPulseStrip.vue'
import FeaturedCruiseShelf from '@/components/FeaturedCruiseShelf.vue'
import DestinationGrid from '@/components/DestinationGrid.vue'

// Canonical destination options for the planner bar (CP-34 ids).
const PLANNER_DESTINATIONS = [
  { id: 'alaska', label: 'Alaska' },
  { id: 'caribbean', label: 'Caribbean' },
  { id: 'bahamas', label: 'Bahamas' },
  { id: 'bermuda', label: 'Bermuda' },
  { id: 'mexico', label: 'Mexican Riviera' },
  { id: 'europe', label: 'Europe' },
  { id: 'mediterranean', label: 'Mediterranean' }
]

const QUICK_CHIPS = [
  { id: 'alaska', label: 'Alaska' },
  { id: 'caribbean', label: 'Caribbean' },
  { id: 'bahamas', label: 'Bahamas' },
  { id: 'europe', label: 'Europe' }
]

export default {
  name: 'Home',
  components: {
    HotDealShelf,
    BestDealShelf,
    MarketPulseStrip,
    FeaturedCruiseShelf,
    DestinationGrid
  },
  data() {
    return {
      plannerDestination: '',
      plannerMonth: '',
      plannerDestinations: PLANNER_DESTINATIONS,
      quickChips: QUICK_CHIPS
    }
  },
  computed: {
    // CP-142: Home deal shelves follow the single runtime flag from /api/config.
    affiliateEnabled() {
      return affiliateState.enabled
    },
    monthOptions() {
      const out = []
      const now = new Date()
      const startYear = now.getFullYear()
      const startMonth = now.getMonth()
      for (let i = 0; i < 12; i++) {
        const d = new Date(startYear, startMonth + i, 1)
        const year = d.getFullYear()
        const month = d.getMonth()
        out.push({
          value: `${year}-${String(month + 1).padStart(2, '0')}`,
          label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        })
      }
      return out
    }
  },
  methods: {
    submitPlanner() {
      const query = {}
      if (this.plannerDestination) query.destination = this.plannerDestination
      if (this.plannerMonth) query.month = this.plannerMonth
      this.$router.push({ path: '/cruises', query })
    },
    goToDestination(id) {
      this.$router.push({ path: '/cruises', query: { destination: id } })
    }
  }
}
</script>

<style scoped>
.home-page {
  margin: 0;
  padding: 0;
}

.hero {
  /* Pin brand deep teals so dark-theme token swaps cannot break on-hero contrast */
  background: linear-gradient(135deg, #0B5B63 0%, #083F45 100%);
  color: #ffffff;
  padding: var(--space-6) var(--space-5);
  margin: calc(-1 * var(--space-5));
  margin-bottom: var(--space-6);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-5);
  align-items: center;
}

.hero-content {
  position: relative;
  z-index: 1;
  max-width: 640px;
}

.hero-eyebrow {
  font-size: var(--text-small);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.88);
  margin-bottom: var(--space-3);
}

.hero-headline {
  font-family: var(--font-heading);
  font-size: clamp(24px, 5vw, 36px);
  font-weight: 700;
  line-height: var(--line-height-tight);
  margin: 0 0 var(--space-4);
  /* Beat global theme.css h1 { color: var(--color-text) } — WCAG AA on teal (≥7.8:1) */
  color: #ffffff;
}

.hero-subline {
  font-size: var(--text-body);
  color: rgba(255, 255, 255, 0.92);
  margin: 0 0 var(--space-5);
  line-height: var(--line-height-body);
}

.hero-planner-bar {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--cs-card);
  border: 1px solid var(--cs-card-border);
  box-shadow: var(--shadow-card-hover);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-3);
}

.planner-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  flex: 1;
}

.planner-field-icon {
  color: var(--cs-signal-teal);
  font-size: 1rem;
}

.planner-field-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--cs-muted);
  width: 100%;
}

.planner-select {
  width: 100%;
  min-height: var(--touch-min);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-body);
  font-size: var(--text-body);
  box-sizing: border-box;
}

.planner-select:focus {
  outline: none;
  border-color: var(--cs-signal-teal);
  box-shadow: 0 0 0 2px var(--cs-signal-mist);
}

.planner-submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: var(--touch-min);
  padding: var(--space-3) var(--space-5);
  background: var(--cs-fill);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-body);
  font-weight: 600;
  font-size: var(--text-body);
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--transition-normal);
}

.planner-submit-btn:hover {
  background: var(--cs-fill-hover);
}

.planner-submit-btn:focus-visible {
  outline: 2px solid white;
  outline-offset: 2px;
}

.hero-quick-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.quick-chips-label {
  font-size: var(--text-small);
  color: rgba(255, 255, 255, 0.9);
}

.quick-chip {
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  padding: 4px 12px;
  font-size: var(--text-small);
  border: 1px solid rgba(255, 255, 255, 0.25);
  cursor: pointer;
  font-family: var(--font-body);
  transition: background var(--transition-fast);
}

.quick-chip:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Desktop: horizontal planner bar */
@media (min-width: 768px) {
  .hero-planner-bar {
    flex-direction: row;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
  }

  .planner-field-label {
    width: auto;
  }
}

.hero-visual {
  display: none;
  position: relative;
  z-index: 0;
  pointer-events: none;
  justify-content: center;
  align-items: center;
  min-height: 220px;
}

.hero-visual-art {
  width: min(100%, 440px);
  height: auto;
  display: block;
  filter: drop-shadow(0 12px 28px rgba(8, 63, 69, 0.35));
}

/* Desktop fold: intentional art fills the right half */
@media (min-width: 900px) {
  .hero {
    grid-template-columns: minmax(0, 1.05fr) minmax(280px, 0.95fr);
    padding: var(--space-7) var(--space-6);
    column-gap: var(--space-6);
  }

  .hero-content {
    max-width: none;
  }

  .hero-visual {
    display: flex;
  }
}

.section-title {
  font-family: var(--font-heading);
  font-size: var(--text-h2);
  color: var(--color-text);
  margin: 0 0 var(--space-5);
  text-align: center;
}

.how-it-works {
  padding: var(--space-6) 0;
  border-bottom: 1px solid var(--color-border-light);
}

.steps {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 140px;
}

.step-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--color-primary-light);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-bottom: var(--space-2);
}

.step-label {
  font-weight: 600;
  color: var(--color-text);
  font-size: var(--text-body);
}

.step-desc {
  font-size: var(--text-small);
  color: var(--color-text-muted);
  margin-top: var(--space-1);
}

.step-arrow {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  opacity: 0.7;
}

.features {
  padding: var(--space-6) 0;
}

/* CP-125: fixed column counts (1 → 2 → 4) so the four quick links always fill
   whole rows; Price alerts (.feature-card--wide) spans the full row below them. */
.feature-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-5);
}

@media (min-width: 640px) {
  .feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .feature-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.feature-card {
  background: var(--color-surface);
  border-radius: var(--radius-md);
  padding: var(--space-5);
  box-shadow: var(--shadow-card);
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
  border: 1px solid var(--color-border-light);
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-card-hover);
}

.feature-card:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.feature-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: var(--color-primary-light);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-bottom: var(--space-4);
}

.feature-title {
  font-family: var(--font-heading);
  font-size: var(--text-h3);
  color: var(--color-text);
  margin: 0 0 var(--space-2);
}

.feature-desc {
  font-size: var(--text-body);
  color: var(--color-text-muted);
  line-height: var(--line-height-body);
  margin: 0 0 var(--space-4);
  flex: 1;
}

.feature-link {
  font-size: var(--text-small);
  font-weight: 600;
  color: var(--color-primary);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.feature-card:hover .feature-link {
  color: var(--color-primary-hover);
}

.feature-card--wide {
  grid-column: 1 / -1;
}

/* Tablet+: full-width band — icon | title + description | link */
@media (min-width: 640px) {
  .feature-card--wide {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "icon title link"
      "icon desc  link";
    column-gap: var(--space-5);
    row-gap: var(--space-1);
    align-items: center;
  }

  .feature-card--wide .feature-icon {
    grid-area: icon;
    margin-bottom: 0;
  }

  .feature-card--wide .feature-title {
    grid-area: title;
    margin: 0;
  }

  .feature-card--wide .feature-desc {
    grid-area: desc;
    margin: 0;
  }

  .feature-card--wide .feature-link {
    grid-area: link;
    white-space: nowrap;
  }
}

@media (max-width: 768px) {
  .hero {
    margin: calc(-1 * var(--space-5));
    margin-bottom: var(--space-5);
    padding: var(--space-5);
  }
}

/* CP-272: below 768 .main-content pads 16px a side (App.vue), not 24px, so a -24px side bleed made
   the hero 8px wider than the screen (398px page at 390). Bleed by exactly the container padding
   (-16px) and pad the sides 16px so the text keeps its old on-screen inset. Top, bottom, 768 and
   wider are unchanged. */
@media (max-width: 767px) {
  .hero {
    margin-left: calc(-1 * var(--space-4));
    margin-right: calc(-1 * var(--space-4));
    padding-left: var(--space-4);
    padding-right: var(--space-4);
  }
}

@media (max-width: 768px) {

  .hero-visual {
    display: none;
  }

  .steps {
    flex-direction: column;
    gap: var(--space-3);
  }

  .step-arrow {
    transform: rotate(90deg);
  }
}
</style>
